import {
  MATRIX_TYPE_FILENAME,
  TabMenuPotentialMatrixV3,
} from "../../constants/AppConstants";
import {
  MATRIX_DEVELOPMENT_LABEL,
  MATRIX_PERFORMANCE_LABEL,
  MATRIX_GENERAL_LABEL,
  DOWNLOAD_LABEL,
  POTENTIAL_MATRIXV3_LABEL,
  NO_CONFIG_LABEL,
  DOWNLOAD_DISABLED_TOOLTIP_LABEL,
} from "../../constants/Dictionary";
import { ReactComponent as DevelopmentIcon } from "../../icons/arrowUpRight.svg";
import { ReactComponent as PerformanceIcon } from "../../icons/analytics.svg";
import { ReactComponent as GeneralIcon } from "../../icons/sparkle.svg";
import { ReactComponent as DownloadIcon } from "../../icons/download.svg";
import { useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { useIsFetching } from "@tanstack/react-query";
import { setTabsPotentialMatrix } from "../../stores/actions/potentialMatrixV3.actions";
import { useAppSelector } from "../../hooks/store.hook";
import { Tab } from "../../components/Tab/Tab";
import { useEditing } from "../../providers/editing.provider";
import {
  StyledMessageContainer,
  StyledTabRightActions,
} from "./PotentialMatrixV3.styled";
import { usePotentialMatrixFilters } from "../../hooks/usePotentialMatrixFilters.hook";
import { Select, SelectVariantEnum } from "../../components/core/Select/Select";
import { InputSizes } from "../../components/core/Input/Input";
import { PotentialMatrixFilterKeyEnum } from "../../types/api/filters.type";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../components/core/Button/Button";
import { useBreadCrumb } from "../../hooks/useBreadCrumbs.hooks";
import { theme } from "../../constants/StyledComponents";
import { useDimensions } from "../../hooks/useDimensions.hook";
import { MessageBox } from "../../components/MessageBox/MessageBox";
import { DevelopmentMatrix } from "./DevelopmentMatrix/DevelopmentMatrix";
import {
  COLLABORATORS_LIMIT,
  MatrixViewModeV3,
  TypeMatrixEnum,
} from "../../constants/PotentialMatrixConstants";
import { PageNames, TabMenu } from "../../types/AppTypes";
import { PerformanceMatrix } from "./PerformanceMatrix/PerformanceMatrix";
import { GeneralMatrix } from "./GeneralMatrix/GeneralMatrix";
import { Loader } from "../../components/core/Loader/Loader";
import {
  Tooltip,
  TooltipOpenModeEnum,
} from "../../components/core/Tooltip/Tooltip";
import generatePDF from "react-to-pdf";
import {
  useMatrixDevelopmentDownload,
  usePotentialMatrixDevelopment,
} from "../../hooks/potentialMatrix/usePotentialMatrixDevelopment.hook";
import {
  InternalGradeStat,
  MatrixDevelopmentItem,
  MatrixPerformanceItem,
} from "../../types/api/potentialMatrix.type";
import {
  useMatrixPerformanceDownload,
  usePotentialMatrixPerformance,
} from "../../hooks/potentialMatrix/usePotentialMatrixPerfomance.hook";

export const PotentialMatrixV3 = () => {
  const dispatch = useDispatch();
  const { confirmLeave } = useEditing();
  const { width } = useDimensions();
  const { tabsPotentialMatrix } = useAppSelector(
    (state) => state.potentialMatrixV3,
  );
  const { config } = useAppSelector((state) => state.config);
  const [selectedViewMode, setSelectedViewMode] = useState<MatrixViewModeV3>(
    MatrixViewModeV3.GLOBAL,
  );
  const [showDownloadPreview, setShowDownloadPreview] = useState(false);
  const matrixDownloadRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadDevelopmentData, setDownloadDevelopmentData] = useState<
    MatrixDevelopmentItem[]
  >([]);
  const [downloadPerformanceData, setDownloadPerformanceData] = useState<
    MatrixPerformanceItem[]
  >([]);
  const [
    downloadDevelopmentInternalGradeStats,
    setDownloadDevelopmentInternalGradeStats,
  ] = useState<InternalGradeStat[]>([]);

  const [isMatrixDetailsOpen, setIsMatrixDetailsOpen] = useState(false);

  const configExists = config && config.length > 0;

  const yearOptions =
    config
      ?.map((item) => ({
        value: item.year,
        label: item.year.toString(),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 2) || [];

  const currentYear = yearOptions[0]?.value;
  const previousYear = yearOptions[1]?.value;

  const { filters, handleFilters, createQueryParams } =
    usePotentialMatrixFilters({
      currentYear,
      previousYear,
      internalGrade: ["all"],
    });

  const isMatrixQueryLoading =
    useIsFetching({
      queryKey: [
        tabsPotentialMatrix === TabMenuPotentialMatrixV3.PERFORMANCE
          ? "get-potential-matrix-performance"
          : "get-potential-matrix-development",
      ],
    }) > 0;

  const { mutateAsync: fetchDownloadDevelopmentData } =
    useMatrixDevelopmentDownload({
      filters,
    });
  const { mutateAsync: fetchDownloadPerformanceData } =
    useMatrixPerformanceDownload({
      filters,
    });

  const { data: developmentData } = usePotentialMatrixDevelopment({
    filters,
    createQueryParams: () => createQueryParams(COLLABORATORS_LIMIT.DEVELOPMENT),
    enabled: tabsPotentialMatrix === TabMenuPotentialMatrixV3.DEVELOPMENT,
  });

  const { data: performanceData } = usePotentialMatrixPerformance({
    filters,
    createQueryParams: () => createQueryParams(COLLABORATORS_LIMIT.PERFORMANCE),
    enabled: tabsPotentialMatrix === TabMenuPotentialMatrixV3.PERFORMANCE,
  });
  //NOTE: verificare se api cambia path

  const tabMenu: TabMenu[] = [
    {
      id: TabMenuPotentialMatrixV3.DEVELOPMENT,
      label: MATRIX_DEVELOPMENT_LABEL,
      icon: <DevelopmentIcon />,
    },
    {
      id: TabMenuPotentialMatrixV3.PERFORMANCE,
      label: MATRIX_PERFORMANCE_LABEL,
      icon: <PerformanceIcon />,
    },
    {
      id: TabMenuPotentialMatrixV3.GENERAL,
      label: MATRIX_GENERAL_LABEL,
      icon: <GeneralIcon />,
    },
  ];

  const handleDownloadMatrix = async (matrixType: TabMenuPotentialMatrixV3) => {
    if (!matrixDownloadRef.current) return;

    await new Promise((resolve) => setTimeout(resolve, 1000));

    const element = matrixDownloadRef.current;
    const contentWidthPx = element.scrollWidth;
    const contentHeightPx = element.scrollHeight;
    const MARGIN_MM = 6;
    const pxToMm = (px: number) => (px * 25.4) / 96;

    await generatePDF(() => element, {
      filename: `potential-matrix-${MATRIX_TYPE_FILENAME[matrixType]}-${filters.year}.pdf`,
      method: "save",
      page: {
        margin: MARGIN_MM,
        format: [pxToMm(contentWidthPx), pxToMm(contentHeightPx)],
      },
      canvas: {
        mimeType: "image/png",
        qualityRatio: 1,
        useCORS: true,
      },
      overrides: {
        pdf: {
          compress: true,
        },
        canvas: {
          useCORS: true,
        },
      },
    });
  };

  const getDownloadLimit = (matrixData?: { count: number }[]) =>
    matrixData?.reduce((max, item) => Math.max(max, item.count), 0) ?? 0;

  const loadDownload = async () => {
    if (tabsPotentialMatrix == null) return;

    try {
      setIsDownloading(true);
      setShowDownloadPreview(true);

      if (tabsPotentialMatrix === TabMenuPotentialMatrixV3.DEVELOPMENT) {
        const downloadLimit = getDownloadLimit(developmentData);
        const result = await fetchDownloadDevelopmentData(downloadLimit);
        setDownloadDevelopmentData(result.data);
        setDownloadDevelopmentInternalGradeStats(result.internalGradeStats);
      }

      if (tabsPotentialMatrix === TabMenuPotentialMatrixV3.PERFORMANCE) {
        const downloadLimit = getDownloadLimit(performanceData);
        const result = await fetchDownloadPerformanceData(downloadLimit);
        setDownloadPerformanceData(result);
      }
      await handleDownloadMatrix(tabsPotentialMatrix);
    } finally {
      setShowDownloadPreview(false);
      setIsDownloading(false);
    }
  };

  const isDownloadEnabled = () => {
    if (isMatrixDetailsOpen) {
      return false;
    }

    if (isMatrixQueryLoading) {
      return false;
    }

    if (
      selectedViewMode === MatrixViewModeV3.COLLABORATOR &&
      filters.practice.length === 1
    ) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (tabMenu.length > 0 && tabsPotentialMatrix === null) {
      dispatch(setTabsPotentialMatrix(TabMenuPotentialMatrixV3.DEVELOPMENT));
    }
  }, [tabMenu, tabsPotentialMatrix, dispatch]);

  useBreadCrumb([
    {
      name: POTENTIAL_MATRIXV3_LABEL,
      pathName: PageNames.POTENTIAL_MATRIXV3,
    },
  ]);

  const handleTabSelected = (option: TabMenuPotentialMatrixV3) => {
    setShowDownloadPreview(false);
    setIsMatrixDetailsOpen(false);
    dispatch(setTabsPotentialMatrix(option));
  };

  const renderDevelopmentTab = () => (
    <DevelopmentMatrix
      filters={filters}
      createQueryParams={() =>
        createQueryParams(COLLABORATORS_LIMIT.DEVELOPMENT)
      }
      handleFilters={handleFilters}
      previousYear={previousYear}
      showDownloadPreview={showDownloadPreview}
      selectedViewMode={selectedViewMode}
      setSelectedViewMode={setSelectedViewMode}
      isDownloadEnabled={isDownloadEnabled()}
      downloadRef={matrixDownloadRef}
      downloadData={downloadDevelopmentData}
      downloadInternalGradeStats={downloadDevelopmentInternalGradeStats}
      onDetailsViewChange={setIsMatrixDetailsOpen}
    />
  );

  const renderPerformanceTab = () => (
    <PerformanceMatrix
      filters={filters}
      createQueryParams={() =>
        createQueryParams(COLLABORATORS_LIMIT.PERFORMANCE)
      }
      handleFilters={handleFilters}
      previousYear={previousYear}
      selectedViewMode={selectedViewMode}
      setSelectedViewMode={setSelectedViewMode}
      onDetailsViewChange={setIsMatrixDetailsOpen}
      isDownloadEnabled={isDownloadEnabled()}
      downloadRef={matrixDownloadRef}
      downloadData={downloadPerformanceData}
      showDownloadPreview={showDownloadPreview}
    />
  );

  const renderGeneralTab = () => (
    <GeneralMatrix
      filters={filters}
      handleFilters={handleFilters}
      previousYear={previousYear}
      createQueryParams={() => createQueryParams(COLLABORATORS_LIMIT.GENERAL)}
    />
  );

  return (
    <Tab
      tabSelected={tabsPotentialMatrix!}
      setTabSelected={handleTabSelected}
      tabMenu={tabMenu}
      confirmLeave={confirmLeave}
      rightContent={
        <StyledTabRightActions>
          <div className="select-year-container">
            <Select
              variant={SelectVariantEnum.DEFAULT}
              onChange={(yearOption) => {
                handleFilters(
                  PotentialMatrixFilterKeyEnum.YEAR,
                  yearOption as number,
                );
              }}
              size={InputSizes.SMALL}
              options={yearOptions}
              value={filters.year}
              allowClear={false}
            />
          </div>
          {isDownloading ? (
            <Loader loading={isDownloading} size={20} />
          ) : (
            filters.type.value === TypeMatrixEnum.FINAL_EVALUATION && (
              <Tooltip
                message={DOWNLOAD_DISABLED_TOOLTIP_LABEL}
                hide={isDownloadEnabled()}
                openMode={TooltipOpenModeEnum.CLICK}
                width={250}
              >
                <Button
                  label={
                    width > theme.breakpoints.XL ? DOWNLOAD_LABEL : undefined
                  }
                  variant={ButtonVariants.PRIMARY}
                  size={ButtonSizes.SMALL}
                  icon={<DownloadIcon />}
                  disabled={!isDownloadEnabled()}
                  onClick={loadDownload}
                />
              </Tooltip>
            )
          )}
        </StyledTabRightActions>
      }
    >
      {!configExists ? (
        <StyledMessageContainer>
          <MessageBox message={NO_CONFIG_LABEL} />
        </StyledMessageContainer>
      ) : (
        <>
          {tabsPotentialMatrix === TabMenuPotentialMatrixV3.DEVELOPMENT &&
            renderDevelopmentTab()}
          {tabsPotentialMatrix === TabMenuPotentialMatrixV3.PERFORMANCE &&
            renderPerformanceTab()}
          {tabsPotentialMatrix === TabMenuPotentialMatrixV3.GENERAL &&
            renderGeneralTab()}
        </>
      )}
    </Tab>
  );
};
